import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { EASE_OUT } from "../theme";

interface ToastProps {
  /** null hides the toast. */
  message: string | null;
  /** Harness hook — e.g. "engagement-action-toast". */
  testId?: string;
  className?: string;
}

/** Inline confirmation strip (extracted from the engagement ActionBar). */
export function Toast({ message, testId, className = "" }: ToastProps) {
  return (
    <AnimatePresence>
      {message ? (
        <motion.div
          data-testid={testId}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: EASE_OUT }}
          className={`rounded-md bg-primary px-3 py-1.5 text-center font-body text-[11px] text-on-primary ${className}`}
        >
          {message}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/** Self-clearing toast message state. `show(msg)` restarts the timer. */
export function useToast(durationMs = 2200) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((msg: string) => {
    setMessage(msg);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), durationMs);
  }, [durationMs]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { message, show };
}
